/* Owner ERP — shared UI + i18n (design system: /app/design_guidelines.json) */
import React from 'react';
import { FONT_SANS } from '../../theme/brand';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Crown } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

export const TEAL = ['#0D9488', '#14B8A6', '#5EEAD4', '#334155', '#94A3B8', '#134e4a'];

export const TXT = {
  en: {
    nav_overview: 'Overview', nav_divisions: 'Divisions', nav_members: 'Members & Roles',
    nav_swish: 'Swish Accounts', nav_reports: 'Reports', nav_audit: 'Audit Log', nav_billing: 'Billing / Plan',
    owner: 'Owner', back_to_app: 'Business app',
    range_7d: 'Last 7 days', range_30d: 'Last 30 days', range_90d: 'Last 90 days', range_ytd: 'Year to date',
    kpi_collected: 'Total collected', kpi_tx: 'Transactions', kpi_avg: 'Avg. payment', kpi_payers: 'Unique payers',
    collections_over_time: 'Collections over time', recent_activity: 'Recent activity',
    by_division: 'Collections by division', payment_mix: 'Payment mix',
    mix_app2app: 'Swish App2App', mix_guest_qr: 'Guest QR',
    seats_plan: 'Seats & Plan', seats_used: 'seats used', divisions_used: 'divisions used',
    swish_used: 'Swish accounts', manage_plan: 'Manage plan', pending_inv: 'Pending invitations',
    view_all: 'View all', all_divisions: 'All divisions', guest: 'Guest', no_data: 'No data in this period',
    divisions_title: 'Divisions & Events', divisions_sub: 'Money always lands in an org-owned Swish number',
    new_sub: 'New division / event', edit: 'Edit', delete: 'Delete', save: 'Save', cancel: 'Cancel',
    name: 'Name', description: 'Description', type: 'Type', parent: 'Parent (nesting)', none: 'None',
    payout_swish: 'Payout Swish', payout_mode: 'Payout selection', mode_locked: 'Locked (Owner pins)',
    mode_admin_choice: 'Admin picks from pool', collected: 'Collected', status: 'Status',
    require_guest_details: 'Require guest name and phone',
    require_guest_details_hint: 'When off, guests can pay without entering name or phone',
    th_admin: 'Admin', th_mods: 'Moderators', status_active: 'Active', status_deleted: 'Deleted',
    confirm_delete: 'Delete this sub-account? Transactions are kept and relocated to the root account.',
    created: 'Created', division: 'Division', event: 'Event',
    members_title: 'Members & Roles', invite_member: 'Invite member', role: 'Role', scope: 'Scope',
    email: 'Email', phone: 'Phone', display_name: 'Display name (optional)',
    role_admin: 'Division admin', role_moderator: 'Moderator', role_revisor: 'Accountant / Revisor',
    whole_org: 'Whole organisation', send_invite: 'Send invitation',
    st_pending: 'Pending', st_accepted: 'Accepted', st_expired: 'Expired',
    remove: 'Remove', cancel_invite: 'Cancel invite', change_role: 'Change role',
    delivery_in_app: 'in-app', delivery_email: 'email', delivery_sms: 'SMS', delivery_failed: 'delivery failed',
    yes: 'Yes', no: 'No', st_deleted: 'deleted',
    confirm_remove: 'Remove this member from the organisation?', expires: 'Expires',
    email_or_phone_required: 'Enter an email or phone number',
    swish_title: 'Swish account pool', swish_sub: 'Delegates can only select from this pool — never add external numbers',
    add_account: 'Add account', merchant_number: 'Swish merchant number', primary: 'Primary',
    set_primary: 'Set primary', confirm_del_swish: 'Remove this Swish account?',
    reports_title: 'Reports', export_csv: 'Export CSV', direction: 'Direction', received: 'Received',
    sent: 'Sent', all: 'All', date: 'Date', amount: 'Amount', from_to: 'From / To', sub_account: 'Sub-account',
    audit_title: 'Organisation audit trail', audit_sub: 'Every privileged action by invited users',
    action: 'Action', actor: 'Actor', when: 'When', details: 'Details', load_more: 'Load more',
    billing_title: 'Billing & Plan', current_plan: 'Current plan', per_month: '/month', per_year: '/year',
    usage: 'Usage', plan_history: 'Payment history', choose_plan: 'Choose plan', current: 'Current',
    unlimited: 'Unlimited', billing_monthly: 'Monthly', billing_annual: 'Annual',
    no_history: 'No payments yet', renews: 'Renews', plan_changed: 'Plan updated',
    loading: 'Loading…', error_load: 'Could not load data', not_business: 'The Owner ERP requires a Business account.',
    go_home: 'Go to app', saved: 'Saved', invited: 'Invitation sent', removed: 'Removed', cancelled: 'Cancelled',
    st_suspended: 'Suspended', suspend: 'Suspend', reactivate: 'Reactivate', resend: 'Resend invite',
    confirm_suspend: 'Suspend this member? They lose access to this organisation immediately and their seat is freed. You can reactivate them later.',
    manage_member: 'Manage member', save_changes: 'Save changes',
    member_suspended: 'Member suspended', member_reactivated: 'Member reactivated', invite_resent: 'Invitation resent',
    view_reports: 'View reports', manage_team: 'Manage team', team_of: 'Team of',
    no_team: 'No one is assigned to this division yet', add_moderator: 'Add moderator',
    close: 'Close', invite_by_email: 'Email or phone',
    selected_count: 'selected', bulk_change_role: 'Change role', clear_selection: 'Clear selection',
    bulk_done: '{ok} updated · {failed} skipped', apply: 'Apply', keep_scope: 'Keep current scope', select_all: 'Select all',
    confirm_bulk_remove: 'Remove the selected members? Pending invites are cancelled and active members lose access.',
    confirm_bulk_suspend: 'Suspend the selected members? They lose access and their seats are freed.',
    upgrade_title: 'Unlock more with a higher plan', upgrade_cta: 'See plans & upgrade', maybe_later: 'Not now',
    cap_divisions: "You've used every division your current plan includes. Upgrade to add more — or create an event under an existing division for free.",
    cap_seats: 'Every team seat on your current plan is taken. Upgrade to invite more admins, moderators and revisors.',
    cap_swish: "You've reached the Swish accounts your current plan allows. Upgrade to connect more payout numbers.",
    create_event_instead: 'Create an event instead',
    organisation: 'Organisation', owner_erp: 'Owner ERP', tx_short: 'tx',
    account: 'Account', logout: 'Log out', profile: 'Profile',
    profile_title: 'Business profile', profile_sub: 'Manage your organisation details',
    company_name: 'Company name', org_number: 'Org. number', contact_name: 'Contact name', address: 'Address',
    swish_reminder_title: 'Add a Swish account first',
    swish_reminder_desc: 'Payments land on org-owned Swish numbers. Add at least one before inviting members and delegating divisions.',
    add_swish_account: 'Add Swish account',
    upload_logo: 'Upload logo', remove: 'Remove', company_logo: 'Company logo',
    audit_actions: {
      org_member_invited: 'Member invited', org_invite_accepted: 'Invitation accepted',
      org_invite_auto_attached: 'Invitation auto-linked', org_invite_rejected: 'Invitation rejected',
      org_invite_cancelled: 'Invitation cancelled', org_member_removed: 'Member removed',
      org_member_role_changed: 'Role changed', org_sub_account_created: 'Division / event created',
      org_event_created: 'Event created', org_event_updated: 'Event updated',
      org_payout_swish_changed: 'Payout Swish changed',
      org_member_suspended: 'Member suspended', org_member_reactivated: 'Member reactivated',
      org_invite_resent: 'Invitation resent',
    },
    audit_keys: {
      role: 'Role', delivery: 'Delivery', name: 'Name', email: 'Email', phone: 'Phone',
      scope: 'Scope', scope_type: 'Scope', from_role: 'From', to_role: 'To',
      plan: 'Plan', plan_name: 'Plan', sub_account_name: 'Sub-account', type: 'Type', status: 'Status',
      invite_email: 'Email', invite_phone: 'Phone', existing_user: 'Existing user',
      parent_sub_account_id: 'Parent', swish_account_id: 'Swish account',
      parent: 'Parent', swish_account: 'Swish account',
      merchant_number: 'Merchant no.', description: 'Description',
    },
  },
  sv: {
    nav_overview: 'Översikt', nav_divisions: 'Divisioner', nav_members: 'Medlemmar & Roller',
    nav_swish: 'Swish-konton', nav_reports: 'Rapporter', nav_audit: 'Granskningslogg', nav_billing: 'Fakturering / Plan',
    owner: 'Ägare', back_to_app: 'Företagsappen',
    range_7d: 'Senaste 7 dagarna', range_30d: 'Senaste 30 dagarna', range_90d: 'Senaste 90 dagarna', range_ytd: 'Hittills i år',
    kpi_collected: 'Totalt insamlat', kpi_tx: 'Transaktioner', kpi_avg: 'Snittbetalning', kpi_payers: 'Unika betalare',
    collections_over_time: 'Insamlingar över tid', recent_activity: 'Senaste aktivitet',
    by_division: 'Insamlat per division', payment_mix: 'Betalningsmix',
    mix_app2app: 'Swish App2App', mix_guest_qr: 'Gäst-QR',
    seats_plan: 'Platser & Plan', seats_used: 'platser använda', divisions_used: 'divisioner använda',
    swish_used: 'Swish-konton', manage_plan: 'Hantera plan', pending_inv: 'Väntande inbjudningar',
    view_all: 'Visa alla', all_divisions: 'Alla divisioner', guest: 'Gäst', no_data: 'Ingen data för perioden',
    divisions_title: 'Divisioner & Event', divisions_sub: 'Pengarna landar alltid på ett Swish-nummer som ägs av organisationen',
    new_sub: 'Ny division / event', edit: 'Redigera', delete: 'Ta bort', save: 'Spara', cancel: 'Avbryt',
    name: 'Namn', description: 'Beskrivning', type: 'Typ', parent: 'Förälder (nästling)', none: 'Ingen',
    payout_swish: 'Utbetalnings-Swish', payout_mode: 'Utbetalningsval', mode_locked: 'Låst (Ägaren väljer)',
    mode_admin_choice: 'Admin väljer från poolen', collected: 'Insamlat', status: 'Status',
    require_guest_details: 'Kräv gästens namn och telefon',
    require_guest_details_hint: 'Av: gäster kan betala utan att ange namn eller telefon',
    th_admin: 'Admin', th_mods: 'Moderatorer', status_active: 'Aktiv', status_deleted: 'Borttagen',
    confirm_delete: 'Ta bort detta underkonto? Transaktioner behålls och flyttas till huvudkontot.',
    created: 'Skapad', division: 'Division', event: 'Event',
    members_title: 'Medlemmar & Roller', invite_member: 'Bjud in medlem', role: 'Roll', scope: 'Omfattning',
    email: 'E-post', phone: 'Telefon', display_name: 'Visningsnamn (valfritt)',
    role_admin: 'Divisionsadmin', role_moderator: 'Moderator', role_revisor: 'Revisor',
    whole_org: 'Hela organisationen', send_invite: 'Skicka inbjudan',
    st_pending: 'Väntande', st_accepted: 'Accepterad', st_expired: 'Utgången',
    remove: 'Ta bort', cancel_invite: 'Återkalla', change_role: 'Ändra roll',
    delivery_in_app: 'i appen', delivery_email: 'e-post', delivery_sms: 'SMS', delivery_failed: 'leverans misslyckades',
    yes: 'Ja', no: 'Nej', st_deleted: 'borttagen',
    confirm_remove: 'Ta bort denna medlem från organisationen?', expires: 'Går ut',
    email_or_phone_required: 'Ange e-post eller telefonnummer',
    swish_title: 'Swish-kontopool', swish_sub: 'Delegater kan endast välja från poolen — aldrig lägga till externa nummer',
    add_account: 'Lägg till konto', merchant_number: 'Swish-handelsnummer', primary: 'Primärt',
    set_primary: 'Gör primärt', confirm_del_swish: 'Ta bort detta Swish-konto?',
    reports_title: 'Rapporter', export_csv: 'Exportera CSV', direction: 'Riktning', received: 'Mottaget',
    sent: 'Skickat', all: 'Alla', date: 'Datum', amount: 'Belopp', from_to: 'Från / Till', sub_account: 'Underkonto',
    audit_title: 'Organisationens granskningslogg', audit_sub: 'Varje privilegierad åtgärd av inbjudna användare',
    action: 'Åtgärd', actor: 'Utförare', when: 'När', details: 'Detaljer', load_more: 'Ladda fler',
    billing_title: 'Fakturering & Plan', current_plan: 'Nuvarande plan', per_month: '/månad', per_year: '/år',
    usage: 'Användning', plan_history: 'Betalningshistorik', choose_plan: 'Välj plan', current: 'Nuvarande',
    unlimited: 'Obegränsat', billing_monthly: 'Månadsvis', billing_annual: 'Årsvis',
    no_history: 'Inga betalningar ännu', renews: 'Förnyas', plan_changed: 'Plan uppdaterad',
    loading: 'Laddar…', error_load: 'Kunde inte ladda data', not_business: 'Ägar-ERP kräver ett företagskonto.',
    go_home: 'Till appen', saved: 'Sparat', invited: 'Inbjudan skickad', removed: 'Borttagen', cancelled: 'Återkallad',
    st_suspended: 'Avstängd', suspend: 'Stäng av', reactivate: 'Återaktivera', resend: 'Skicka igen',
    confirm_suspend: 'Stäng av denna medlem? De förlorar omedelbart åtkomst till organisationen och platsen frigörs. Du kan återaktivera dem senare.',
    manage_member: 'Hantera medlem', save_changes: 'Spara ändringar',
    member_suspended: 'Medlem avstängd', member_reactivated: 'Medlem återaktiverad', invite_resent: 'Inbjudan skickad igen',
    view_reports: 'Visa rapporter', manage_team: 'Hantera team', team_of: 'Team för',
    no_team: 'Ingen är tilldelad denna division ännu', add_moderator: 'Lägg till moderator',
    close: 'Stäng', invite_by_email: 'E-post eller telefon',
    selected_count: 'valda', bulk_change_role: 'Ändra roll', clear_selection: 'Rensa val',
    bulk_done: '{ok} uppdaterade · {failed} hoppades över', apply: 'Tillämpa', keep_scope: 'Behåll nuvarande omfattning', select_all: 'Välj alla',
    confirm_bulk_remove: 'Ta bort de valda medlemmarna? Väntande inbjudningar återkallas och aktiva medlemmar förlorar åtkomst.',
    confirm_bulk_suspend: 'Stäng av de valda medlemmarna? De förlorar åtkomst och deras platser frigörs.',
    upgrade_title: 'Lås upp mer med en högre plan', upgrade_cta: 'Se planer & uppgradera', maybe_later: 'Inte nu',
    cap_divisions: 'Du har använt alla divisioner som din nuvarande plan innehåller. Uppgradera för att lägga till fler — eller skapa ett event under en befintlig division utan kostnad.',
    cap_seats: 'Alla platser i din nuvarande plan är upptagna. Uppgradera för att bjuda in fler administratörer, moderatorer och revisorer.',
    cap_swish: 'Du har nått antalet Swish-konton som din nuvarande plan tillåter. Uppgradera för att koppla fler utbetalningsnummer.',
    create_event_instead: 'Skapa ett event istället',
    organisation: 'Organisation', owner_erp: 'Ägar-ERP', tx_short: 'tx',
    account: 'Konto', logout: 'Logga ut', profile: 'Profil',
    profile_title: 'Företagsprofil', profile_sub: 'Hantera organisationens uppgifter',
    company_name: 'Företagsnamn', org_number: 'Org.nummer', contact_name: 'Kontaktnamn', address: 'Adress',
    swish_reminder_title: 'Lägg till ett Swish-konto först',
    swish_reminder_desc: 'Betalningar landar på Swish-nummer som ägs av organisationen. Lägg till minst ett innan du bjuder in medlemmar och delegerar divisioner.',
    add_swish_account: 'Lägg till Swish-konto',
    upload_logo: 'Ladda upp logotyp', remove: 'Ta bort', company_logo: 'Företagslogotyp',
    audit_actions: {
      org_member_invited: 'Medlem inbjuden', org_invite_accepted: 'Inbjudan accepterad',
      org_invite_auto_attached: 'Inbjudan länkad automatiskt', org_invite_rejected: 'Inbjudan avböjd',
      org_invite_cancelled: 'Inbjudan återkallad', org_member_removed: 'Medlem borttagen',
      org_member_role_changed: 'Roll ändrad', org_sub_account_created: 'Division / event skapat',
      org_event_created: 'Event skapat', org_event_updated: 'Event uppdaterat',
      org_payout_swish_changed: 'Utbetalnings-Swish ändrad',
      org_member_suspended: 'Medlem avstängd', org_member_reactivated: 'Medlem återaktiverad',
      org_invite_resent: 'Inbjudan skickad igen',
    },
    audit_keys: {
      role: 'Roll', delivery: 'Leverans', name: 'Namn', email: 'E-post', phone: 'Telefon',
      scope: 'Omfattning', scope_type: 'Omfattning', from_role: 'Från', to_role: 'Till',
      plan: 'Plan', plan_name: 'Plan', sub_account_name: 'Underkonto', type: 'Typ', status: 'Status',
      invite_email: 'E-post', invite_phone: 'Telefon', existing_user: 'Befintlig användare',
      parent_sub_account_id: 'Förälder', swish_account_id: 'Swish-konto',
      parent: 'Förälder', swish_account: 'Swish-konto',
      merchant_number: 'Handelsnr.', description: 'Beskrivning',
    },
  },
};

export const auditActionLabel = (action, t) =>
  (t.audit_actions && t.audit_actions[action]) || (action || '').replace(/_/g, ' ');

export const auditDetail = (details, t) => {
  const skip = ['actor_name', 'membership_id', 'sub_account_id', 'scope_id', 'user_id'];
  const val = (k, v) => {
    if (k === 'existing_user') return (String(v) === 'True' || v === true) ? t.yes : t.no;
    if (k === 'delivery') return t['delivery_' + v] || v;
    return v;
  };
  return Object.entries(details || {})
    .filter(([k]) => !skip.includes(k))
    .map(([k, v]) => `${(t.audit_keys && t.audit_keys[k]) || k}: ${val(k, v)}`)
    .join(' · ');
};

export const fmtKr = (n) =>
  new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(Math.round(n || 0)) + ' kr';

export const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB',
      { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export const WORK_SANS = { fontFamily: FONT_SANS };

export function Fonts() {
  return <style>{`@import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');`}</style>;
}

export function Panel({ children, className = '', testId }) {
  return (
    <div data-testid={testId} className={`bg-white border border-slate-200 rounded-xl shadow-sm ${className}`}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, sub }) {
  return (
    <div>
      <h2 className="text-[15px] font-semibold tracking-tight" style={WORK_SANS}>{children}</h2>
      {sub && <p className="text-[12px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export function Spark({ data, color = '#0D9488' }) {
  const d = (data && data.length ? data : [0, 0]).map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width="100%" height={44}>
      <AreaChart data={d} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`osg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2}
              fill={`url(#osg-${color.replace('#', '')})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function KpiCard({ title, value, deltaPct, spark, testId }) {
  const up = (deltaPct ?? 0) >= 0;
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-1 min-w-0">
      <div className="text-[12px] font-medium text-slate-500">{title}</div>
      <div className="flex items-end justify-between gap-2">
        <div className="text-[24px] leading-none font-semibold text-slate-900 tabular-nums" style={WORK_SANS}>{value}</div>
        {deltaPct !== null && deltaPct !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-[12px] font-semibold px-1.5 py-0.5 rounded-md ${up ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
            {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{Math.abs(deltaPct)}%
          </span>
        )}
      </div>
      {spark && <div className="-mx-1 mt-1"><Spark data={spark} /></div>}
    </div>
  );
}

export function StatusBadge({ value, t }) {
  const map = {
    ACTIVE: ['bg-teal-50 text-teal-700 border-teal-200', t.status_active],
    accepted: ['bg-teal-50 text-teal-700 border-teal-200', t.st_accepted],
    pending: ['bg-amber-50 text-amber-700 border-amber-200', t.st_pending],
    expired: ['bg-slate-100 text-slate-500 border-slate-200', t.st_expired],
    suspended: ['bg-rose-50 text-rose-700 border-rose-200', t.st_suspended],
  };
  const [cls, label] = map[value] || ['bg-slate-100 text-slate-600 border-slate-200', value];
  return <Badge className={`${cls} border shadow-none font-medium hover:bg-inherit`}>{label}</Badge>;
}

export function RoleBadge({ role, t }) {
  const map = {
    admin: ['bg-teal-50 text-teal-700 border-teal-200', t.role_admin],
    moderator: ['bg-blue-50 text-blue-700 border-blue-200', t.role_moderator],
    revisor: ['bg-amber-50 text-amber-700 border-amber-200', t.role_revisor],
    owner: ['bg-brand-accent/10 text-brand-accent border-brand-accent/25', t.owner],
  };
  const [cls, label] = map[role] || ['bg-slate-100 text-slate-600 border-slate-200', role];
  return <Badge className={`${cls} border shadow-none font-medium hover:bg-inherit`}>{label}</Badge>;
}

export const atCap = (u) => !!u && u.limit !== null && u.limit !== undefined && u.limit >= 0 && (u.used ?? 0) >= u.limit;

export function UpgradeDialog({ open, onOpenChange, t, body, onUpgrade, secondaryLabel, onSecondary }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="owner-upgrade-dialog">
        <DialogHeader>
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center mb-1">
            <Crown size={20} />
          </div>
          <DialogTitle>{t.upgrade_title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button data-testid="owner-upgrade-cta" className="w-full bg-teal-700 hover:bg-teal-800 text-white" onClick={onUpgrade}>
            <Crown size={14} className="mr-1.5" />{t.upgrade_cta}
          </Button>
          {secondaryLabel && (
            <Button data-testid="owner-upgrade-secondary" variant="outline" className="w-full" onClick={onSecondary}>
              {secondaryLabel}
            </Button>
          )}
          <Button data-testid="owner-upgrade-later" variant="ghost" className="w-full text-slate-500" onClick={() => onOpenChange(false)}>
            {t.maybe_later}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Meter({ used, limit, color = 'bg-teal-600' }) {
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null;
  return (
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: pct === null ? '8%' : pct + '%' }} />
    </div>
  );
}
